import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const auth0 = defineConnector({
  identifier: "auth0",
  name: "Auth0",
  tagLine: "Identity platform for developers",
  keywords: "okta iam sso oidc oauth saml identity access management users mfa",
  logo,
  protocol: "http",
  tags: ["security", "identity"],
  defaultUrlPattern: () => "https://[^/]*\\.auth0\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create an Auth0 application",
      items: [
        {
          kind: "markdown",
          content:
            "Sign in to your [Auth0 Dashboard](https://manage.auth0.com/) and navigate to **Applications** > **Create Application**. Choose **Regular Web Application** as the type.",
        },
      ],
    },
    {
      title: "Configure allowed callback URLs",
      items: [
        {
          kind: "markdown",
          content:
            "In your Auth0 application settings, add the following to **Allowed Callback URLs**:",
        },
        {
          kind: "copyable",
          label: "Callback URL",
          value: "{API_URL}/connected-apps/oauth/auth0/callback",
        },
      ],
    },
    {
      title: "Enter your Auth0 domain",
      items: [
        {
          kind: "markdown",
          content:
            "Your Auth0 domain can be found in your application settings (e.g., `your-tenant.auth0.com`).",
        },
        {
          kind: "hostname",
          label: "Auth0 domain",
          placeholder: "your-tenant.auth0.com",
          required: true,
          helperText: "Your Auth0 tenant domain",
          target: { kind: "configuration", field: "domain" },
        },
      ],
    },
    {
      title: "Enter your credentials",
      items: [
        {
          kind: "markdown",
          content:
            "Copy the Client ID and Client Secret from your Auth0 application settings.",
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Define scopes",
      items: [
        {
          kind: "markdown",
          content:
            "Add [scopes](https://auth0.com/docs/get-started/apis/scopes) in a space separated list below. Default scopes include `openid profile email`.",
        },
        {
          kind: "input",
          label: "Scopes (optional)",
          placeholder: "openid profile email",
          helperText: "Space separated list of desired scopes.",
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Authorize your Auth0 account",
      items: [
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
            { kind: "configuration", field: "domain" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: ({ domain }) => `https://${domain}/authorize`,
    tokenEndpoint: ({ domain }) => `https://${domain}/oauth/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ domain }) => `https://${domain}/userinfo`,
  },
  publicEnvVars: ({ domain }) => ({
    AUTH0_URL: domain ? `https://${domain}` : "",
  }),
});
